import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import type {
  CalendarEntryFormTimed,
  DeleteReminderByUserResponse,
  EventApiResponse,
  EventCreateApiRequest,
  PaginatedResponse,
  RecurrenceRule,
  RecurrenceScope,
  ReminderData,
  ReminderEmailVariants,
  ReminderEmailVariantKey,
} from "@/lib/humhub-api/types";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

function sanitizeCalendarEntryForm(
  form: Partial<EventCreateApiRequest["CalendarEntryForm"]> | undefined,
):
  | Partial<
      Omit<
        EventCreateApiRequest["CalendarEntryForm"],
        "scope" | "recurrence" | "reminders"
      >
    >
  | undefined {
  if (!form) return undefined;
  const { scope: _scope, recurrence: _recurrence, ...rest } = form;
  return rest;
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce<Partial<T>>((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key as keyof T] = value as T[keyof T];
    }
    return acc;
  }, {});
}

function buildUpdatePayload(event: EventUpdateRequest): EventUpdateRequest {
  const { CalendarEntryForm, scope, CalendarEntry, formName } = event;
  const sanitizedForm = sanitizeCalendarEntryForm(CalendarEntryForm);

  const filteredForm = sanitizedForm
    ? stripUndefined(sanitizedForm)
    : undefined;

  const payload: EventUpdateRequest = {};

  if (CalendarEntry && Object.keys(CalendarEntry).length > 0) {
    payload.CalendarEntry = CalendarEntry;
  }

  if (filteredForm && Object.keys(filteredForm).length > 0) {
    payload.CalendarEntryForm = filteredForm;
  }

  if (scope !== undefined || CalendarEntryForm?.scope !== undefined) {
    payload.scope = scope ?? CalendarEntryForm?.scope;
  }

  if (formName !== undefined) {
    payload.formName = formName;
  }

  return payload;
}

async function withApiErrorHandling<T>(
  operation: () => Promise<T>,
  options?: { suppressLogStatuses?: number[] },
): Promise<T> {
  try {
    return await operation();
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      if (!error.response) {
        throw new Error("Please check your connection");
      }
      const suppressLogStatuses = options?.suppressLogStatuses ?? [];
      if (!suppressLogStatuses.includes(error.response.status)) {
        console.error("API Error:", error.response.data);
      }
      throw error;
    }
    throw new Error("Unexpected error occurred");
  }
}

export interface CustomReminderPayload {
  calendar_entry_id: number;
  reminder_id?: number;
  subject?: string;
  body_html?: string;
  body_text?: string;
  email_variants?: ReminderEmailVariants | null;
  email_variants_json?: string | null;
  participation_states?: number[];
  include_creator?: boolean;
  include_self?: boolean;
  test_audience?: ReminderEmailVariantKey | null;
  test_mode?: boolean;
  status?: number;
  processed_at?: number | null;
  updated_at?: number | null;
  is_custom?: boolean;
  message?: string;
}

type LegacySuccessResponse = {
  success?: boolean;
  code?: number;
  message?: string;
  data?: unknown;
  error?: string;
};

export type CustomReminderResponse =
  | CustomReminderPayload
  | LegacySuccessResponse;

type CalendarEntryShape = EventCreateApiRequest extends {
  CalendarEntry: infer C;
}
  ? C
  : never;
type CalendarEntryFormShape = EventCreateApiRequest extends {
  CalendarEntryForm: infer F;
}
  ? F
  : never;
export type EventUpdateRequest = {
  scope?: RecurrenceScope;
  CalendarEntry?: Partial<CalendarEntryShape>;
  CalendarEntryForm?: Partial<CalendarEntryFormShape>;
  formName?: string;
};

export type RecomposeEventResponse = {
  scope: RecurrenceScope;
  kept_entry_ids: number[];
  deleted_entry_ids: number[];
  created_entry_ids: number[];
};

export const eventsApi = {
  acceptParticipants: async (
    eventId: number,
    userIds: number[],
    scope: RecurrenceScope = "single",
  ) => {
    return withApiErrorHandling(async () => {
      const response = await api.post(
        `${API_URLS.CALENDAR}/entry/${eventId}/accept-participants`,
        {
          user_ids: userIds,
          scope,
        },
      );
      return response.data;
    });
  },
  declineParticipants: async (
    eventId: number,
    userIds: number[],
    scope: RecurrenceScope = "single",
  ) => {
    return withApiErrorHandling(async () => {
      const response = await api.post(
        `${API_URLS.CALENDAR}/entry/${eventId}/decline-participants`,
        { user_ids: userIds, scope },
      );
      return response.data;
    });
  },
  clearParticipants: async (
    eventId: number,
    userIds: number[],
    scope: RecurrenceScope = "single",
  ) => {
    return withApiErrorHandling(async () => {
      const response = await api.post(
        `${API_URLS.CALENDAR}/entry/${eventId}/clear-participants`,
        { user_ids: userIds, scope },
      );
      return response.data;
    });
  },
  inviteParticipants: async (
    eventId: number,
    userIds: number[],
    scope: RecurrenceScope = "single",
  ) => {
    return withApiErrorHandling(async () => {
      const response = await api.post(
        `${API_URLS.CALENDAR}/entry/${eventId}/invite-participants`,
        { user_ids: userIds, scope },
      );
      return response.data;
    });
  },
  uninviteParticipants: async (
    eventId: number,
    userIds: number[],
    scope: RecurrenceScope = "single",
  ) => {
    return withApiErrorHandling(async () => {
      const response = await api.post(
        `${API_URLS.CALENDAR}/entry/${eventId}/uninvite-participants`,
        { user_ids: userIds, scope },
      );
      return response.data;
    });
  },
  sendReminderEmail: async (
    eventId: number,
    payload: {
      reminderId?: number | null;
      email_subject: string;
      email_body_html: string;
      email_body_text?: string;
      email_variants?: ReminderEmailVariants | null;
      subject?: string;
      body?: string;
      save_for_reminder: boolean;
      save_for_entry?: boolean;
      send_now?: boolean;
      participation_states?: number[];
      include_creator?: boolean;
      include_self?: boolean;
      test_audience?: ReminderEmailVariantKey | null;
      test_mode?: boolean;
    },
  ): Promise<CustomReminderResponse> => {
    return withApiErrorHandling(async () => {
      const { reminderId, ...restPayload } = payload;
      const requestPayload = {
        ...restPayload,
        reminder_id: reminderId ?? undefined,
        subject: restPayload.subject ?? restPayload.email_subject,
        body: restPayload.body ?? restPayload.email_body_html,
      };
      const response = await api.post<CustomReminderResponse>(
        `${API_URLS.CALENDAR}/entry/${eventId}/email-participants`,
        requestPayload,
      );
      return response.data;
    });
  },
  getAllEvents: async (
    page = 1,
    perPage = 20,
    startDate = new Date().toISOString().split("T")[0],
    additionalParams = {},
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    return withApiErrorHandling(async () => {
      const params = {
        page,
        limit: perPage,
        "start-date": startDate,
        ...additionalParams,
      };

      const response = await api.get<PaginatedResponse<EventApiResponse>>(
        API_URLS.CALENDAR,
        {
          params,
        },
      );
      return response.data;
    });
  },
  createEvent: async (
    containerId: number,
    event: EventCreateApiRequest,
    files: File[],
  ): Promise<EventApiResponse> => {
    return withApiErrorHandling(async () => {
      // TODO(proposal): createEvent の引数をフラットDTO（例: ClientEventCreateRequest）にし、
      //  ここでAPI形（EventCreateApiRequest）および FormData への変換を完結させると
      //  呼び出し側の型推論が容易になり、all_day/時刻の分岐も境界層に閉じ込められる。
      const formData = new FormData();

      // Add event data
      formData.append("CalendarEntry[title]", event.CalendarEntry.title);
      formData.append(
        "CalendarEntry[description]",
        event.CalendarEntry.description,
      );
      formData.append("CalendarEntry[color]", event.CalendarEntry.color);
      formData.append(
        "CalendarEntry[all_day]",
        event.CalendarEntry.all_day.toString(),
      );
      formData.append(
        "CalendarEntry[participation_mode]",
        event.CalendarEntry.participation_mode.toString(),
      );
      formData.append(
        "CalendarEntry[max_participants]",
        event.CalendarEntry.max_participants.toString(),
      );
      formData.append(
        "CalendarEntry[allow_decline]",
        event.CalendarEntry.allow_decline.toString(),
      );
      formData.append(
        "CalendarEntry[allow_maybe]",
        event.CalendarEntry.allow_maybe.toString(),
      );
      formData.append(
        "CalendarEntry[participant_info]",
        event.CalendarEntry.participant_info,
      );

      // Add form data
      formData.append(
        "CalendarEntryForm[start_date]",
        event.CalendarEntryForm.start_date,
      );
      formData.append(
        "CalendarEntryForm[end_date]",
        event.CalendarEntryForm.end_date,
      );

      // 時刻フィールドは all_day=0（時刻指定）のときのみ送信
      if (event.CalendarEntry.all_day === 0) {
        const timedEntryForm =
          event.CalendarEntryForm as CalendarEntryFormTimed;
        formData.append(
          "CalendarEntryForm[start_time]",
          timedEntryForm.start_time,
        );
        formData.append("CalendarEntryForm[end_time]", timedEntryForm.end_time);
      }
      formData.append(
        "CalendarEntryForm[timeZone]",
        event.CalendarEntryForm.timeZone,
      );
      formData.append(
        "CalendarEntryForm[location]",
        event.CalendarEntryForm.location,
      );
      formData.append(
        "CalendarEntryForm[language]",
        event.CalendarEntryForm.language,
      );
      formData.append(
        "CalendarEntryForm[is_draft]",
        event.CalendarEntryForm.is_draft.toString(),
      );
      formData.append(
        "CalendarEntryForm[online_location]",
        event.CalendarEntryForm.online_location,
      );
      formData.append(
        "CalendarEntryForm[location_type]",
        String(event.CalendarEntryForm.location_type),
      );
      formData.append(
        "CalendarEntryForm[forceJoin]",
        event.CalendarEntryForm.forceJoin.toString(),
      );
      formData.append(
        "CalendarEntryForm[topics]",
        event.CalendarEntryForm.topics,
      );
      formData.append(
        "CalendarEntryForm[is_public]",
        event.CalendarEntryForm.is_public.toString(),
      );
      formData.append(
        "CalendarEntryForm[hidden]",
        event.CalendarEntryForm.hidden.toString(),
      );
      if (event.CalendarEntryForm.recurrence) {
        formData.append(
          "CalendarEntryForm[recurrence]",
          JSON.stringify(event.CalendarEntryForm.recurrence),
        );
      }
      if (event.CalendarEntryForm.scope) {
        formData.append(
          "CalendarEntryForm[scope]",
          event.CalendarEntryForm.scope,
        );
      }

      // Add reminders
      event.CalendarEntryForm.reminders.forEach((reminder, index) => {
        formData.append(
          `CalendarEntryForm[reminders][${index}][type]`,
          reminder.type.toString(),
        );
        formData.append(
          `CalendarEntryForm[reminders][${index}][unit]`,
          reminder.unit.toString(),
        );
        formData.append(
          `CalendarEntryForm[reminders][${index}][value]`,
          reminder.value.toString(),
        );
        if (reminder.email_subject) {
          formData.append(
            `CalendarEntryForm[reminders][${index}][email_subject]`,
            reminder.email_subject,
          );
        }
        if (reminder.email_body_html) {
          formData.append(
            `CalendarEntryForm[reminders][${index}][email_body_html]`,
            reminder.email_body_html,
          );
        }
        if (reminder.email_body_text) {
          formData.append(
            `CalendarEntryForm[reminders][${index}][email_body_text]`,
            reminder.email_body_text,
          );
        }
      });

      // Add files
      files.forEach((file) => {
        formData.append("files[]", file);
      });

      const response = await api.post<EventApiResponse>(
        `${API_URLS.CALENDAR}/container/${containerId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    });
  },
  getGroupEvents: async (
    page = 1,
    perPage = 20,
    short = true,
    containerId?: number,
    startDate?: string,
    endDate?: string,
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    return withApiErrorHandling(async () => {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit: perPage,
        short,
        "container-id": containerId,
        "group-member-only": true,
      };

      if (startDate) {
        params["start-date"] = startDate;
      }
      if (endDate) {
        params["end-date"] = endDate;
      }

      const response = await api.get<PaginatedResponse<EventApiResponse>>(
        API_URLS.CALENDAR,
        {
          params,
        },
      );
      return response.data;
    });
  },
  getEventById: async (id: number): Promise<EventApiResponse> => {
    return withApiErrorHandling(async () => {
      const response = await api.get<EventApiResponse>(
        `${API_URLS.CALENDAR}/entry/${id}`,
      );
      return response.data;
    });
  },
  getPublicEventByCode: async (code: string): Promise<EventApiResponse> => {
    return withApiErrorHandling(
      async () => {
        const encodedCode = encodeURIComponent(code);
        const response = await api.get<EventApiResponse>(
          `${API_URLS.CALENDAR}/entry/${encodedCode}/public-view`,
        );

        return response.data;
      },
      { suppressLogStatuses: [404] },
    );
  },
  respondToEvent: async (
    id: number,
    respondType: number,
    scope: RecurrenceScope = "single",
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      await api.post(`${API_URLS.CALENDAR}/entry/${id}/respond`, {
        type: respondType,
        scope,
      });
    });
  },
  createEventReminders: async (
    eventId: number,
    reminders: ReminderData[],
    scope?: RecurrenceScope,
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      await api.post(`${API_URLS.CALENDAR}/entry/${eventId}/reminder`, {
        reminders,
        scope,
      });
    });
  },
  propagateEventReminders: async (
    eventId: number,
    scope: RecurrenceScope = "single",
  ): Promise<{
    scope: RecurrenceScope;
    created_ids: number[];
    failed_entry_ids: number[];
    skipped_entry_ids: number[];
  }> => {
    return withApiErrorHandling(async () => {
      const response = await api.post(
        `${API_URLS.CALENDAR}/entry/${eventId}/reminders/propagate`,
        {
          scope,
        },
      );
      return response.data;
    });
  },
  recomposeEvent: async (
    eventId: number,
    payload: {
      recurrence?: RecurrenceRule | null;
      scope?: RecurrenceScope;
      mode?: "until_extend" | "until_shrink";
      rebuild_future?: boolean;
    },
  ): Promise<RecomposeEventResponse> => {
    return withApiErrorHandling(async () => {
      const response = await api.post<RecomposeEventResponse>(
        `${API_URLS.CALENDAR}/entry/${eventId}/recompose`,
        {
          scope: payload.scope ?? "single",
          recurrence: payload.recurrence ?? null,
          rebuild_future: payload.rebuild_future ?? undefined,
        },
        payload.mode ? { params: { mode: payload.mode } } : undefined,
      );
      return response.data;
    });
  },

  deleteAllEventReminders: async (
    eventId: number,
    scope?: RecurrenceScope,
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      await api.delete(
        `${API_URLS.CALENDAR}/entry/${eventId}/user-all-reminders`,
        { params: scope ? { scope } : undefined },
      );
    });
  },
  updateEvent: async (
    id: number,
    event: EventUpdateRequest,
  ): Promise<EventApiResponse> => {
    return withApiErrorHandling(async () => {
      const payload = buildUpdatePayload(event);

      const response = await api.put<EventApiResponse>(
        `${API_URLS.CALENDAR}/entry/${id}`,
        payload,
      );
      return response.data;
    });
  },
  deleteEvent: async (
    id: number,
    scope: "single" | "future" | "all" = "single",
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      await api.delete(`${API_URLS.CALENDAR}/entry/${id}`, {
        params: { scope },
      });
    });
  },
  replaceEventFile: async (
    id: number,
    file: File,
    scope?: RecurrenceScope,
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      const formData = new FormData();
      formData.append("files[]", file);

      await api.post(
        `${API_URLS.CALENDAR}/entry/${id}/replace-file`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          params: scope ? { scope } : undefined,
        },
      );
    });
  },
  removeEventFile: async (
    id: number,
    fileId: number,
    scope: RecurrenceScope = "single",
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      await api.delete(
        `${API_URLS.CALENDAR}/entry/${id}/remove-file/${fileId}`,
        {
          params: { scope },
        },
      );
    });
  },
  getMyEvents: async (
    page = 1,
    perPage = 20,
    short = true,
    startDate?: string,
    endDate?: string,
    userParticipation?: string,
    additionalParams?: Record<string, string | number | boolean | undefined>,
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    return withApiErrorHandling(async () => {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit: perPage,
        short,
        ...additionalParams,
      };

      if (startDate) {
        params["start-date"] = startDate;
      }
      if (endDate) {
        params["end-date"] = endDate;
      }
      if (userParticipation) {
        params["user-participation"] = userParticipation;
      }

      const response = await api.get<PaginatedResponse<EventApiResponse>>(
        API_URLS.CALENDAR,
        {
          params,
        },
      );
      return response.data;
    });
  },
  getRegisteredEvents: async (
    page = 1,
    perPage = 20,
    short = true,
    startDate?: string,
    endDate?: string,
    userParticipation?: string,
  ): Promise<PaginatedResponse<EventApiResponse>> => {
    return withApiErrorHandling(async () => {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit: perPage,
        short,
        "user-participation": userParticipation,
      };

      if (startDate) {
        params["start-date"] = startDate;
      }
      if (endDate) {
        params["end-date"] = endDate;
      }

      const response = await api.get<PaginatedResponse<EventApiResponse>>(
        API_URLS.CALENDAR,
        {
          params,
        },
      );
      return response.data;
    });
  },
  updateAllParticipantsReminders: async (
    eventId: number,
    reminders: ReminderData[],
    scope?: RecurrenceScope,
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      await api.post(
        `${API_URLS.CALENDAR}/entry/${eventId}/all-participants-reminder`,
        {
          reminders,
          scope,
        },
      );
    });
  },
  deleteAllParticipantsReminders: async (
    eventId: number,
    scope?: RecurrenceScope,
  ): Promise<void> => {
    return withApiErrorHandling(async () => {
      await api.delete(
        `${API_URLS.CALENDAR}/entry/${eventId}/all-participants-reminder`,
        { params: scope ? { scope } : undefined },
      );
    });
  },
  deleteReminderByUser: async (
    eventId: number,
    userId: number,
    scope?: RecurrenceScope,
  ): Promise<DeleteReminderByUserResponse> => {
    return withApiErrorHandling(async () => {
      const response = await api.delete<DeleteReminderByUserResponse>(
        `${API_URLS.CALENDAR}/entry/${eventId}/reminder-by-user`,
        {
          params: {
            user_id: userId,
            scope,
          },
        },
      );
      return response.data;
    });
  },
  fetchCustomReminderTemplate: async (
    eventId: number,
    reminderId: number,
    options?: { signal: AbortSignal },
  ): Promise<CustomReminderPayload> => {
    return withApiErrorHandling(async () => {
      const response = await api.get<CustomReminderPayload>(
        `${API_URLS.CALENDAR}/entry/${eventId}/custom-reminder`,
        { signal: options?.signal, params: { reminder_id: reminderId } },
      );
      return response.data;
    });
  },
  fetchEntryEmailTemplate: async (
    eventId: number,
    options?: { signal: AbortSignal },
  ): Promise<{
    email_subject?: string | null;
    email_body_html?: string | null;
    email_body_text?: string | null;
    subject?: string | null;
    body_html?: string | null;
    body_text?: string | null;
    is_custom?: boolean;
  }> => {
    return withApiErrorHandling(async () => {
      const response = await api.get<{
        email_subject?: string | null;
        email_body_html?: string | null;
        email_body_text?: string | null;
        subject?: string | null;
        body_html?: string | null;
        body_text?: string | null;
        is_custom?: boolean;
      }>(`${API_URLS.CALENDAR}/entry/${eventId}/email-template`, {
        signal: options?.signal,
      });
      return response.data;
    });
  },
};
